import { useCallback, useEffect } from 'react';
import { trim } from 'lodash';
import { useDebouncedCallback } from 'use-debounce';

import { EXTRACT_ANNOTATIONS_MAX_CHARS } from 'src/data/brief';
import {
  doesToolSupportPromptInstruction,
  isMultiPromptTool,
  isRewriteGenerationTool,
} from 'src/data/generationTool';
import * as events from 'src/lib/events';
import { createStateSelectorHook } from 'src/lib/sweetState';
import { useErrorHandler, useTrackingEvent } from 'src/services';
import { BrandVoicesStore, PerformanceStore, useCurrentProjectId } from 'src/store';

import { LocalStore, useAnnotationActions, useLocalActions } from '../store';
import { serializeMultiPromptFields } from '../utils';

const DEBOUNCE_TIMEOUT = 1000;

const usePerformanceState = createStateSelectorHook(PerformanceStore, ['lastExtractionDateMap']);
const useBrandVoicesState = createStateSelectorHook(BrandVoicesStore, ['brandVoicesAssets']);
const useLocalState = createStateSelectorHook(LocalStore, [
  'briefContent',
  'generationTool',
  'rewriteText',
  'rewriteProductName',
  'selectedInstructionsOption',
  'suggestedAnnotationsOrigin',
  'extractedBriefs',
  'isExtractingBrief',
  'freestyleToolContent',
  'multiPromptFields',
  'promptImage',
  'promptDocument',
]);

export const useFetchAnnotationsData = () => {
  const localActions = useLocalActions();
  const annotationActions = useAnnotationActions();
  const trackingEvent = useTrackingEvent();
  const handleError = useErrorHandler();
  const {
    briefContent,
    generationTool,
    rewriteText,
    rewriteProductName,
    extractedBriefs,
    isExtractingBrief,
    freestyleToolContent,
    multiPromptFields,
    promptImage,
    promptDocument,
  } = useLocalState();

  const projectId = useCurrentProjectId();
  const { lastExtractionDateMap } = usePerformanceState();
  const { brandVoicesAssets } = useBrandVoicesState();

  const trackExtractedFromBrief = useCallback(() => {
    trackingEvent(events.DOCUMENT.annotationExtractedFromBrief, {
      projectId: String(projectId),
    });
  }, [trackingEvent, projectId]);

  const debounceFetchSuggestedAnnotations = useDebouncedCallback(
    async (
      briefContent,
      isPrompt,
      isMultiPrompt,
      promptImage,
      promptDocument,
      freestyleToolAllInstructionsData,
      urlExtractions
    ) => {
      try {
        // Replace suggested annotations, but fetch new ones only if brief is valid
        if (
          ((isMultiPrompt || isPrompt) && briefContent.body !== '') ||
          localActions.validateBrief({ silent: true })
        ) {
          if (!briefContent && !promptImage && !promptDocument) {
            return false;
          }

          annotationActions.setLoadingSuggestions(true);
          const params = {
            briefContent,
            promptImage,
            promptDocument,
            freestyleToolAllInstructionsData,
            urlExtractions,
          };
          const fetched = await annotationActions.fetchSuggestedAnnotations(params);
          if (fetched) {
            trackExtractedFromBrief();
          }
        } else {
          annotationActions.clearSuggestedAnnotations();
        }
      } catch (error) {
        handleError(error);
      } finally {
        if (!localActions.getIsExtractingBrief()) {
          annotationActions.setLoadingSuggestions(false);
        }
      }
    },
    DEBOUNCE_TIMEOUT
  );

  const debounceFetchRecomposeAnnotations = useDebouncedCallback(
    async (rewriteText, rewriteProductName) => {
      try {
        await annotationActions.fetchSuggestedCopyAnnotations(rewriteText, rewriteProductName);
      } catch (error) {
        handleError(error);
      } finally {
        annotationActions.setLoadingSuggestions(false);
      }
    },
    DEBOUNCE_TIMEOUT
  );

  useEffect(() => {
    // We don't need "suggested annotations" in Infinity "prompt" mode
    if (localActions.inExtensionPromptMode()) {
      return;
    }

    localActions.clearTalkingPoints();
    if (
      !trim(rewriteText) ||
      !generationTool ||
      !isRewriteGenerationTool(generationTool) ||
      rewriteText.length > EXTRACT_ANNOTATIONS_MAX_CHARS
    ) {
      return;
    }

    annotationActions.setLoadingSuggestions(true);
    debounceFetchRecomposeAnnotations(rewriteText, rewriteProductName);

    return () => {
      annotationActions.cancelCopyAnnotationsRequest();
    };
  }, [
    annotationActions,
    lastExtractionDateMap,
    briefContent,
    debounceFetchRecomposeAnnotations,
    rewriteText,
    rewriteProductName,
    localActions,
    generationTool,
  ]);

  useEffect(() => {
    if (isRewriteGenerationTool(generationTool)) {
      return;
    }

    // We don't need "suggested annotations" in Infinity "prompt" mode
    if (localActions.inExtensionPromptMode()) {
      return;
    }

    const isPrompt = doesToolSupportPromptInstruction(generationTool);
    const isMultiPrompt = isMultiPromptTool(generationTool);

    const content = isPrompt
      ? {
          body: freestyleToolContent?.allInstructionsData?.serialized,
        }
      : isMultiPrompt
      ? {
          body: serializeMultiPromptFields(multiPromptFields),
        }
      : briefContent;
    // const brandBriefs = brandVoicesAssets?.reduce(
    //   (acc, { brandBriefs }) => [...acc, ...brandBriefs],
    //   []
    // );
    // if (isPrompt && freestyleToolContent?.allInstructionsData?.placeholders) {
    //   content = replacePromptPlaceholders(
    //     content,
    //     freestyleToolContent?.allInstructionsData?.placeholders,
    //     brandBriefs
    //   );
    // }

    if (isExtractingBrief) {
      annotationActions.setLoadingSuggestions(true);
      return;
    }

    const fullBriefContent = { ...content };
    const urlExtractions = [];
    if (fullBriefContent.body) {
      for (const [url, extractedBrief] of Object.entries(extractedBriefs)) {
        fullBriefContent.body = fullBriefContent.body.replace(url, '"' + extractedBrief + '"');
        urlExtractions.push({ url, extractedBrief });
      }
    }

    localActions.clearTalkingPoints();
    annotationActions.setLoadingSuggestions(true);
    debounceFetchSuggestedAnnotations(
      fullBriefContent,
      isPrompt,
      isMultiPrompt,
      promptImage,
      promptDocument,
      freestyleToolContent?.allInstructionsData,
      urlExtractions
    );
  }, [
    briefContent,
    generationTool,
    debounceFetchSuggestedAnnotations,
    annotationActions,
    lastExtractionDateMap,
    localActions,
    extractedBriefs,
    isExtractingBrief,
    freestyleToolContent?.allInstructionsData,
    brandVoicesAssets,
    multiPromptFields,
    promptImage,
    promptDocument,
  ]);
};
